import { isDef, isDefAndNotNull } from '@utils/def';
import { ChangeEvent, FC, useCallback, useEffect, useMemo } from 'react';
import { ErrorCode, FileRejection, useDropzone } from 'react-dropzone';
import { v4 } from 'uuid';

import Button, { Sizes, StyleTypes } from '../Button';
import { ICONS_TYPES } from '../Icon';
import Loading from '../Loading';

import { MAX_FILE_SIZE, MIN_FILE_SIZE } from './constants';
import { FileUploaderVariant, StatusTypes } from './enums';
import {
  AttentionIcon,
  DropContainer,
  DropLabelDescription,
  DropLabelTitle,
  ErrorMessage,
  ErrorMessageContainer,
  FailedIcon,
  FileIcon,
  FileInput,
  FileInputBox,
  FileItemCard,
  FileItemDetails,
  FileItemInfo,
  FileItemName,
  FileItemSize,
  FileItemsContainer,
  FileItemsHead,
  FileItemsHeadTitle,
  FileName,
  InlineUploader,
  InputLabel,
  RemoveIcon,
  RequiredMark,
  Root,
  SuccessIcon,
  UploadIcon,
} from './styles';
import { Props } from './types';
import { acceptToString } from './utils';

const FileUploader: FC<Props> = ({
  accept,
  autoSubmit = false,
  className,
  clearError,
  disabled,
  description,
  error,
  hideSubmitButton = false,
  label = 'Select a file',
  loading = false,
  maxSize = MAX_FILE_SIZE,
  minSize = MIN_FILE_SIZE,
  multi = false,
  name,
  onBlur,
  onChange,
  onSubmit,
  required,
  setError,
  value,
  variant = FileUploaderVariant.dropzone,
}) => {
  const isCompact = variant === FileUploaderVariant.compact;
  const isMulti = useMemo(
    () => (isCompact ? false : Boolean(multi)),
    [isCompact, multi],
  );
  const isDisabled = useMemo(() => disabled || loading, [disabled, loading]);
  const inputAccept = useMemo(() => acceptToString(accept), [accept]);

  const handleDrop = useCallback(
    (files: File[]) => {
      onChange([
        ...value,
        ...files.map((file) => {
          const fileNameSplit = file.name.split('.');
          const hasExt = fileNameSplit.length > 1;
          const nameWithoutExtension = hasExt
            ? fileNameSplit.slice(0, fileNameSplit.length - 1).join('.')
            : file.name;
          const fileExtension = hasExt
            ? fileNameSplit[fileNameSplit.length - 1]
            : '';

          return {
            data: file,
            name: nameWithoutExtension,
            fileExtension,
            id: v4(),
            status: StatusTypes.none,
          };
        }),
      ]);
    },
    [onChange, value],
  );

  const handleDropRejection = useCallback(
    (fileRejections: FileRejection[]) => {
      const [
        {
          errors: [{ code, message }],
        },
      ] = fileRejections;
      let messageFormatted = message;

      if (code === ErrorCode.FileInvalidType) {
        messageFormatted = message.replace(/,/g, ', ');
      }

      setError?.(messageFormatted);
    },
    [setError],
  );

  const handleDropAccepted = useCallback(() => {
    clearError?.();
  }, [clearError]);

  const handleFileNameChange = useCallback(
    (name: string) => {
      onChange(value!.map((file) => ({ ...file, name })));

      if (!name) {
        setError?.('This field is required!');
      } else if (isDefAndNotNull(error)) {
        clearError?.();
      }
    },
    [value, error, onChange, setError, clearError],
  );

  const handleRemoveItem = useCallback(
    (index: number) => () => {
      onChange(value!.filter((_, fileIndex) => fileIndex !== index));
    },
    [value, onChange],
  );

  const handleOnSubmit = useCallback(
    () => onSubmit?.(value),
    [onSubmit, value],
  );

  const handleNativeInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        onChange([]);
        return;
      }

      if (file.size > maxSize) {
        setError?.(
          `File is too large. Max size is ${(maxSize / (1024 * 1024)).toFixed(1)} MB`,
        );
        onChange([]);
        return;
      }
      if (file.size < minSize) {
        setError?.(
          `File is too small. Min size is ${(minSize / 1024).toFixed(0)} KB`,
        );
        onChange([]);
        return;
      }

      const split = file.name.split('.');
      const hasExt = split.length > 1;
      const nameWithoutExtension = hasExt
        ? split.slice(0, -1).join('.')
        : file.name;
      const fileExtension = hasExt ? split[split.length - 1] : '';

      onChange([
        {
          data: file,
          name: nameWithoutExtension,
          fileExtension,
          id: v4(),
          status: StatusTypes.none,
        },
      ]);
      clearError?.();
    },
    [onChange, maxSize, minSize, setError, clearError],
  );

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept,
    disabled: isDisabled || isCompact,
    maxSize,
    minSize,
    multiple: isMulti,
    onDrop: handleDrop,
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejection,
    onFileDialogCancel: onBlur,
  });

  useEffect(() => {
    if (autoSubmit && value.length) {
      onSubmit?.(value);
    }
  }, [autoSubmit, onSubmit, value]);

  return (
    <Root className={className}>
      {label && (
        <InputLabel>
          {required ? (
            <span>
              {label}
              <RequiredMark>*</RequiredMark>
            </span>
          ) : (
            label
          )}
        </InputLabel>
      )}

      {isCompact ? (
        <InlineUploader>
          <FileInputBox htmlFor={name}>
            <FileInput
              id={name}
              name={name}
              accept={inputAccept}
              multiple={false}
              disabled={isDisabled}
              onChange={handleNativeInputChange}
              onBlur={onBlur}
              aria-label={label ?? name}
            />
          </FileInputBox>

          <FileName>
            {!value?.length
              ? 'No file selected'
              : ((): string => {
                  const f = value[0];
                  const base = f?.name || f?.data?.name || '1 file';
                  return f?.fileExtension ? `${base}.${f.fileExtension}` : base;
                })()}
          </FileName>

          {isDefAndNotNull(onSubmit) && (
            <Button
              size={Sizes.small}
              styleType={StyleTypes.success}
              label="Upload File"
              onClick={() => onSubmit?.(value)}
              loading={loading}
              disabled={isDisabled || !value?.length}
            />
          )}
        </InlineUploader>
      ) : (
        <>
          {(multi || (!multi && value.length === 0)) && (
            <DropContainer
              {...getRootProps()}
              disabled={isDisabled}
              error={error}
              isDragActive={isDragActive}
              htmlFor={name}>
              <input {...getInputProps({ onBlur })} />
              <UploadIcon icon={ICONS_TYPES.Upload} size={32} />
              <DropLabelTitle>{label}</DropLabelTitle>
              <DropLabelDescription>
                {isDef(description) ? description : 'Drag and drop file here'}
              </DropLabelDescription>
            </DropContainer>
          )}

          {isDefAndNotNull(value) && value?.length !== 0 && (
            <FileItemsContainer>
              {value?.map((file, index) => {
                const inputValue = isDefAndNotNull(file.name)
                  ? file.name
                  : file.data?.name;

                return (
                  <FileItemCard key={file.id}>
                    <FileItemDetails>
                      <FileIcon icon={ICONS_TYPES.FileDownload} size={32} />
                      <FileItemInfo>
                        <FileItemName>{inputValue}</FileItemName>
                        {isDefAndNotNull(file.data?.size) && (
                          <FileItemSize>
                            {(file.data!.size / 1024).toFixed(1)} KB
                          </FileItemSize>
                        )}
                      </FileItemInfo>
                    </FileItemDetails>
                    {[StatusTypes.none, StatusTypes.failed].includes(
                      file.status,
                    ) && (
                      <RemoveIcon
                        onClick={handleRemoveItem(index)}
                        icon={ICONS_TYPES.Delete}
                        size={14}
                      />
                    )}
                    {file.status === StatusTypes.uploading && (
                      <Loading size={14} />
                    )}
                    {file.status === StatusTypes.done && (
                      <SuccessIcon
                        size={16}
                        icon={ICONS_TYPES.CheckMarkCircle}
                      />
                    )}
                    {file.status === StatusTypes.failed && (
                      <FailedIcon size={16} icon={ICONS_TYPES.Error} />
                    )}
                  </FileItemCard>
                );
              })}
              <FileItemsHead multi={multi}>
                <FileItemsHeadTitle>
                  Selected Files ({value.length})
                </FileItemsHeadTitle>
                {isDefAndNotNull(onSubmit) && !hideSubmitButton && (
                  <Button
                    size={Sizes.small}
                    styleType={StyleTypes.success}
                    label={multi ? 'Upload All Files' : 'Upload File'}
                    onClick={handleOnSubmit}
                    loading={loading}
                  />
                )}
              </FileItemsHead>
            </FileItemsContainer>
          )}
        </>
      )}

      {error && (
        <ErrorMessageContainer>
          <AttentionIcon icon={ICONS_TYPES.Attention} size={20} />
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </Root>
  );
};

export type { Props };
export { StatusTypes };

export default FileUploader;
